/**
 * Phase 7.5 (partial) — language preference.
 *
 * Forward-compat foundation for the eventual i18n layer. Today the
 * choice is persisted to localStorage but does NOT actually swap any
 * UI strings — the app is still pt-BR-only and the SettingsLayout
 * marks the section as preview ("em breve"). When a real i18n
 * library lands (react-i18next or similar), it can subscribe to
 * `useLanguageStore` and the SettingsLayout preview badge can drop.
 *
 * Why ship the stub now: it locks in the storage key and the
 * supported-language list so plug-in i18n later is additive, not a
 * schema rename. Mirror of the theme module's split (pure helpers
 * here, Zustand handle at `@/stores/useLanguageStore`, UI in
 * `SettingsLayout`).
 *
 * v1 supports 3 languages (pt-BR / en-US / es-ES) — the v10.0 plan's
 * "7 languages" target is forward-compatible: extending
 * `SUPPORTED_LANGUAGES` is the only change required.
 */

export type LanguagePreference = "pt-BR" | "en-US" | "es-ES";

export interface LanguageOption {
  value: LanguagePreference;
  label: string;
  /** Native-language self-name for the picker (more recognisable than translated names). */
  nativeLabel: string;
}

export const SUPPORTED_LANGUAGES: readonly LanguageOption[] = [
  { value: "pt-BR", label: "Portuguese (Brazil)", nativeLabel: "Português (Brasil)" },
  { value: "en-US", label: "English (US)", nativeLabel: "English (US)" },
  { value: "es-ES", label: "Spanish (Spain)", nativeLabel: "Español (España)" },
];

const STORAGE_KEY = "language";
const DEFAULT_LANGUAGE: LanguagePreference = "pt-BR";

/**
 * Map a navigator language code (`navigator.language` — e.g. "pt", "pt-BR",
 * "en-GB") to one of the supported languages. Falls back to `DEFAULT_LANGUAGE`
 * when no match.
 *
 * Pure — no DOM access. Tested in `language.test.ts`.
 */
export function matchNavigatorLanguage(navLang: string | undefined | null): LanguagePreference {
  if (!navLang || typeof navLang !== "string") return DEFAULT_LANGUAGE;
  const lower = navLang.toLowerCase();
  // Exact-match first.
  for (const opt of SUPPORTED_LANGUAGES) {
    if (opt.value.toLowerCase() === lower) return opt.value;
  }
  // Then language-family match (e.g. "en-GB" → "en-US"; "pt-PT" → "pt-BR").
  const family = lower.split("-")[0];
  for (const opt of SUPPORTED_LANGUAGES) {
    if (opt.value.toLowerCase().startsWith(`${family}-`)) return opt.value;
  }
  return DEFAULT_LANGUAGE;
}

/** Read the persisted preference. Returns null when nothing is stored or the value is unrecognised. */
export function readStoredLanguage(): LanguagePreference | null {
  if (typeof localStorage === "undefined") return null;
  const v = localStorage.getItem(STORAGE_KEY);
  if (!v) return null;
  for (const opt of SUPPORTED_LANGUAGES) {
    if (opt.value === v) return opt.value;
  }
  return null;
}

/** Persist the preference. Pass `null` to clear (reset to navigator-derived default). */
export function writeStoredLanguage(lang: LanguagePreference | null): void {
  if (typeof localStorage === "undefined") return;
  if (lang === null) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, lang);
}

/**
 * Resolve the effective language at boot: explicit stored value if present,
 * otherwise navigator-derived, otherwise hard default. Mirrors the theme
 * resolver's stored→system→default cascade.
 */
export function resolveEffectiveLanguage(
  stored: LanguagePreference | null | undefined,
  navigatorLang: string | undefined | null,
): LanguagePreference {
  if (stored && SUPPORTED_LANGUAGES.some((o) => o.value === stored)) return stored;
  return matchNavigatorLanguage(navigatorLang);
}
